import {
  pushImagesEvent,
  pushVideoPlayEvent,
} from './dataLayer';
import {
  getAnalyticsContextFromElement,
  getAnalyticsSection,
  getDecanterPageProductMeta,
} from './analyticsContext';

const trackedVideoSources = new WeakMap();

/**
 * Resolve an <img> from a click target (including <picture><source>).
 * @param {EventTarget|null} target
 * @returns {HTMLImageElement|null}
 */
const resolveClickedImage = (target) => {
  if (!target || target.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const el = /** @type {Element} */ (target);
  if (el.tagName === 'IMG') {
    return /** @type {HTMLImageElement} */ (el);
  }

  if (el.tagName === 'SOURCE') {
    const picture = el.closest('picture');
    const img = picture?.querySelector('img');
    return img || null;
  }

  return null;
};

/**
 * @param {HTMLImageElement} img
 * @returns {boolean}
 */
const shouldIgnoreImage = (img) => {
  if (!img || img.hasAttribute('data-analytics-ignore')) {
    return true;
  }

  if (img.closest('[data-analytics-ignore-images]')) {
    return true;
  }

  const w = img.naturalWidth || 0;
  const h = img.naturalHeight || 0;
  if (w > 0 && h > 0 && w < 3 && h < 3) {
    return true;
  }

  return false;
};

/**
 * @param {HTMLImageElement} img
 * @returns {string}
 */
const getImageClickLabel = (img) => {
  const link = img.closest('a');
  const alt = (img.getAttribute('alt') || '').trim();

  if (link) {
    const linkText = link.textContent.replace(/\s+/g, ' ').trim();
    const onlyImg = link.childElementCount === 1 && link.querySelector('img') === img;
    if (onlyImg && linkText === alt) {
      return alt || 'Image';
    }
    if (linkText) {
      return linkText.slice(0, 500);
    }
  }

  const title = (img.getAttribute('title') || '').trim();
  return alt || title || 'Image';
};

/**
 * @param {HTMLVideoElement} video
 * @returns {string}
 */
const getVideoLabel = (video, context = '') => {
  const aria = (video.getAttribute('aria-label') || '').trim();
  if (aria) {
    return aria;
  }

  const title = (video.getAttribute('title') || '').trim();
  if (title) {
    return title;
  }

  const track = video.querySelector('track[kind="captions"], track[kind="subtitles"]');
  const trackLabel = (track?.getAttribute('label') || '').trim();
  if (trackLabel) {
    return trackLabel;
  }

  const src = video.currentSrc || video.src || '';
  if (src.includes('blank.mp4')) {
    return '';
  }

  if (context) {
    return context;
  }

  try {
    const base = src.split('/').pop().split('?')[0];
    return base || 'Video';
  } catch (error) {
    return 'Video';
  }
};

const handleImageClick = (event) => {
  const img = resolveClickedImage(event.target);
  if (!img || shouldIgnoreImage(img)) {
    return;
  }

  if (img.closest('a.digital-cellar__decanter-link')) {
    return;
  }

  const productMeta = getDecanterPageProductMeta();
  pushImagesEvent({
    section: getAnalyticsSection(),
    context: getAnalyticsContextFromElement(img),
    label: getImageClickLabel(img),
    name: productMeta.name,
    id: productMeta.id,
    collectionName: productMeta.collectionName,
  });
};

const handleVideoPlay = (event) => {
  const video = event.target;
  if (!(video instanceof HTMLVideoElement)) {
    return;
  }

  const src = video.currentSrc || video.src || '';
  if (src.includes('blank.mp4')) {
    return;
  }

  if (trackedVideoSources.get(video) === src) {
    return;
  }

  trackedVideoSources.set(video, src);

  const context = getAnalyticsContextFromElement(video);
  const label = getVideoLabel(video, context) || 'Video';
  const productMeta = getDecanterPageProductMeta();

  pushVideoPlayEvent({
    section: getAnalyticsSection(),
    context,
    label,
    name: productMeta.name,
    id: productMeta.id,
    collectionName: productMeta.collectionName,
  });
};

/**
 * Delegated tracking for image clicks and HTML5 video playback (analytics PDFs).
 */
export default function initImageAndVideoAnalytics() {
  if (typeof document === 'undefined') {
    return;
  }

  document.addEventListener('click', handleImageClick);
  document.addEventListener('play', handleVideoPlay, true);
}
